/**
 * Articles published by Foundelo (the SEO autopilot) — the shared, pure part.
 *
 * Foundelo commits one JSON file per article and language to
 * `src/data/foundelo/<lang>/<slug>.json` (and deletes it when an article is
 * unpublished). This module turns one of those files into the same record shape
 * as the hand-written articles in articles.js, so the blog, the article page,
 * the pre-renderer and the sitemap treat both identically.
 *
 * CommonJS on purpose: `scripts/generate-seo-html.js` runs in plain Node and
 * requires it, and the app imports it through webpack. One normaliser means the
 * served HTML and the hydrated page can never disagree about an article.
 * Keep it free of object/array spread: Babel would add ESM helper imports to a
 * CommonJS file and webpack would then see no exports at all.
 */

// The site's own categories and read-time wording, so a Foundelo article sits in
// the blog exactly like a hand-written one in the same language.
const CATEGORY = {
  ar: 'نصائح زواج',
  en: 'islamic marriage guidance',
  fr: 'mariage islamique',
  de: 'islamische eheberatung',
};
const MINUTES = { ar: 'دقائق', en: 'minutes', fr: 'minutes', de: 'Minuten' };
const FAQ_HEADING = { ar: 'أسئلة شائعة', en: 'Frequently asked questions', fr: 'Questions fréquentes', de: 'Häufige Fragen' };
const SOURCES_HEADING = { ar: 'المصادر', en: 'Sources', fr: 'Sources', de: 'Quellen' };

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/i;

const str = (v) => (typeof v === 'string' ? v : '');
const day = (v) => (str(v).match(/^\d{4}-\d{2}-\d{2}/) || [''])[0];
const baseLang = (lang) => str(lang).toLowerCase().split('-')[0] || 'ar';

/**
 * Foundelo writes standard Markdown; the article page renders a small subset
 * (## / ###, **bold**, [links](url), lists, tables, quotes, ---). Everything
 * else is folded into that subset here so no raw `*`, `_`, `####` or `![` ever
 * reaches a reader.
 */
function toSiteMarkdown(md) {
  return str(md)
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/\s+$/, ''))
    // An H1 is the page title, rendered by the page itself.
    .filter((line) => !/^#\s/.test(line))
    // Images: the page has no image block; the alt text alone would read oddly.
    .filter((line) => !/^\s*!\[[^\]]*\]\([^)]*\)\s*$/.test(line))
    .map((line) =>
      line
        .replace(/^#{4,6}\s+/, '### ')
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/__([^_]+)__/g, '**$1**')
        // Single-star / underscore emphasis → plain text (bold `**` untouched).
        .replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1$2')
        .replace(/(^|[\s(])_(?!\s)([^_\n]+?)_(?=[\s).,:;!?]|$)/g, '$1$2')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/^\s*[-*+]\s+/, '- ')
        .replace(/^\s*(\d+)[.)]\s+/, '$1. ')
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** The questions and the sources are part of the visible page (FAQPage needs that). */
function withFaqAndSources(body, faq, sources, lang) {
  let out = body;
  const has = (text) => out.includes(text);
  const questions = faq.filter((f) => !has(f.question));
  if (questions.length) {
    out += `\n\n## ${FAQ_HEADING[lang] || FAQ_HEADING.en}\n`;
    for (const f of questions) out += `\n### ${f.question}\n${f.answer}\n`;
  }
  const missing = sources.filter((s) => !has(s.url));
  if (missing.length) {
    out += `\n\n## ${SOURCES_HEADING[lang] || SOURCES_HEADING.en}\n\n`;
    out += missing.map((s) => `- [${(s.title || s.url).replace(/[[\]]/g, '')}](${s.url})`).join('\n');
  }
  return out.trim();
}

/**
 * The article's hero image ({ url, alt, width, height }), or null: only an https
 * URL counts, and a size is kept only when both sides are positive numbers.
 */
function heroImage(h) {
  if (!h || typeof h !== 'object' || !/^https:\/\//.test(str(h.url))) return null;
  const w = Number(h.width);
  const ht = Number(h.height);
  return {
    url: h.url,
    alt: str(h.alt),
    width: w > 0 && ht > 0 ? Math.round(w) : null,
    height: w > 0 && ht > 0 ? Math.round(ht) : null,
  };
}

/**
 * One Foundelo file → one article record, or null when the file is not a
 * usable article (wrong shape, unsafe slug, no text). Never throws: a bad file
 * must not take the blog or the build down with it.
 */
function normalizeFoundeloArticle(file) {
  if (!file || typeof file !== 'object' || file.foundelo !== 1) return null;
  const slug = str(file.slug);
  const title = str(file.title).trim();
  const lang = baseLang(file.lang);
  if (!SLUG_RE.test(slug) || !title) return null;
  const faq = (Array.isArray(file.faq) ? file.faq : [])
    .filter((f) => f && str(f.question).trim() && str(f.answer).trim())
    .map((f) => ({ question: str(f.question).trim(), answer: str(f.answer).trim() }));
  const sources = (Array.isArray(file.sources) ? file.sources : [])
    .filter((s) => s && /^https?:\/\//.test(str(s.url)))
    .map((s) => ({ url: s.url, title: str(s.title) || null }));
  const body = toSiteMarkdown(file.markdown);
  if (!body) return null;
  const minutes = Number(file.read_minutes) > 0 ? Math.round(Number(file.read_minutes)) : 1;
  const published = day(file.date_published) || day(file.date_modified);
  const record = {
    slug,
    title,
    description: str(file.meta_description) || str(file.llms_summary),
    keywords: str(file.keyword) ? [str(file.keyword)] : [],
    category: CATEGORY[lang] || CATEGORY.en,
    readTime: `${minutes} ${MINUTES[lang] || MINUTES.en}`,
    publishedAt: published,
    lastUpdated: day(file.date_modified) || published,
    faq,
    content: withFaqAndSources(body, faq, sources, lang),
    translationKey: str(file.translation_key) || null,
    image: heroImage(file.hero_image),
    foundelo: true,
  };
  // Only the non-Arabic hand-written articles carry `lang`; Arabic is the default.
  if (lang !== 'ar') record.lang = lang;
  return record;
}

/**
 * Normalised Foundelo articles, newest first, minus any slug a hand-written
 * article already uses (the hand-written one wins: it may carry history).
 */
function foundeloArticles(files, takenSlugs) {
  const taken = new Set(takenSlugs || []);
  const out = [];
  for (const f of files) {
    const a = normalizeFoundeloArticle(f);
    if (!a || taken.has(a.slug)) continue;
    taken.add(a.slug);
    out.push(a);
  }
  return out.sort((x, y) => (y.publishedAt || '').localeCompare(x.publishedAt || ''));
}

/**
 * hreflang for Foundelo articles: a translation set is every live language
 * version sharing a `translation_key` (a removed one has no file, so it drops
 * out by itself). x-default is the Arabic version, the site's main language;
 * otherwise the first. Same shape as TRANSLATION_CLUSTERS in SEO.jsx.
 */
function foundeloClusters(articles) {
  const groups = new Map();
  for (const a of articles) {
    if (!a.translationKey) continue;
    const g = groups.get(a.translationKey) || [];
    g.push(a);
    groups.set(a.translationKey, g);
  }
  const clusters = [];
  for (const g of Array.from(groups.values())) {
    const byLang = new Map();
    for (const a of g) if (!byLang.has(a.lang || 'ar')) byLang.set(a.lang || 'ar', a);
    if (byLang.size < 2) continue;
    const pages = Array.from(byLang.entries()).map(([hrefLang, a]) => ({ hrefLang, path: `/blog/${a.slug}` }));
    const main = byLang.get('ar') || Array.from(byLang.values())[0];
    clusters.push({ xDefault: `/blog/${main.slug}`, pages });
  }
  return clusters;
}

/** `/blog/<slug>` → language, for the non-Arabic Foundelo articles (PAGE_LANG). */
function foundeloPageLang(articles) {
  const out = {};
  for (const a of articles) if (a.lang) out[`/blog/${a.slug}`] = a.lang;
  return out;
}

module.exports = { normalizeFoundeloArticle, foundeloArticles, foundeloClusters, foundeloPageLang, toSiteMarkdown };
